type BaselineCacheKey = `${string}|${number}|${number}`; // 'Reenie Beanie|177|1.25'

export interface FontBoxMetrics {
	ascent: number; // baseline → top of tallest glyph (may include diacritics)
	descent: number; // baseline → bottom of deepest descender
}

export interface FontOptions {
	family: string;
	bold?: boolean;
	italic?: boolean;
	underline?: boolean;
}

const baselineCache = new Map<BaselineCacheKey, number>();
let baseliner: HTMLDivElement | null = null;
let baselineMarker: HTMLSpanElement | null = null;

export const DEFAULT_LINEHEIGHT = 1.25;

export const MIN_FONTSIZE = Math.round(pointToPixel(12));

/**
 * Calculates the baseline offset in pixels for a given font, font size, and line height.
 * The baseline offset is the vertical distance from the top of the text container to the
 * text baseline, which is critical for precise text positioning in SVG or other rendering contexts.
 * The function uses a temporary DOM element to measure the baseline and caches the result
 * to avoid redundant calculations.
 *
 * @param fontFamily - The font family to measure the baseline for.
 * @param fontSize - The font size in pixels.
 * @param lineHeight - The line height multiplier (default: {@link DEFAULT_LINEHEIGHT}).
 * @returns The baseline offset in pixels, measured from the top of the text container.
 */
export function getBaselineOffsetPx(
	fontFamily: string,
	fontSize: number,
	lineHeight = DEFAULT_LINEHEIGHT,
): number {
	/**
	 * Create a cache key based on the fontFamily, fontSize, and lineHeight
	 * to store and retrieve previously calculated baseline offsets.
	 */
	const key: BaselineCacheKey = `${fontFamily}|${fontSize}|${lineHeight}`;

	/**
	 * Check if the baseline offset for this combination is already cached.
	 * If found, return the cached value to avoid recalculation.
	 */
	if (baselineCache.has(key)) {
		return baselineCache.get(key)!;
	}

	if (!baseliner) {
		/**
		 * Create a temporary <div> element to measure the text baseline.
		 * Set styles to ensure accurate measurement:
		 * - Absolute positioning to avoid affecting the page layout.
		 * - Hidden visibility to prevent visual impact.
		 * - Pre-formatted whitespace to preserve text layout.
		 * - Font family, size, and line height as specified.
		 * - Disable ligatures and special font features for consistency.
		 * - Reset padding, margin, and border to eliminate extra space.
		 */
		baseliner = document.createElement('div');
		baseliner.style.position = 'absolute';
		baseliner.style.visibility = 'hidden';
		baseliner.style.whiteSpace = 'pre';
		baseliner.style.textTransform = 'none';
		baseliner.style.font = 'unset';
		baseliner.style.padding = '0';
		baseliner.style.margin = '0';
		baseliner.style.border = '0 solid';
		baseliner.style.left = '-9999px';
		baseliner.style.top = '-9999px';
		/**
		 * Append the wrapper to the document body to enable measurement
		 * of its bounding box and the baseline mark's position.
		 */
		document.body.appendChild(baseliner);
		baseliner.style.fontVariantLigatures = 'no-contextual';
		baseliner.style.fontFeatureSettings = '"calt" 0, "rand" 0';
	}

	baseliner.style.fontFamily = fontFamily;
	baseliner.style.fontSize = `${fontSize}px`;
	baseliner.style.lineHeight = String(lineHeight);
	baseliner.textContent = 'X';

	if (!baselineMarker) {
		/**
		 * Create a zero-sized <span> element to mark the baseline position.
		 * The span is inline-block to ensure it aligns with the text baseline.
		 */
		baselineMarker = document.createElement('span');
		baselineMarker.style.display = 'inline-block';
		baselineMarker.style.width = '0';
		baselineMarker.style.height = '0';
		baselineMarker.style.textTransform = 'none';
		baselineMarker.style.font = 'unset';
		baselineMarker.style.padding = '0';
		baselineMarker.style.margin = '0';
		baselineMarker.style.border = '0 solid';
	}

	baseliner.appendChild(baselineMarker);
	baselineMarker.style.fontVariantLigatures = 'no-contextual';
	baselineMarker.style.fontFeatureSettings = '"calt" 0, "rand" 0';

	/**
	 * Measure the bounding boxes of the wrapper and baseline mark.
	 * Calculate the offset as the difference between the top of the
	 * baseline mark and the top of the wrapper.
	 */
	const wrapperBox = baseliner.getBoundingClientRect();
	const markBox = baselineMarker.getBoundingClientRect();
	const offset = markBox.top - wrapperBox.top;

	baselineCache.set(
		key,
		offset,
	);

	return offset;
}

export function getFontFamily(options: FontOptions): string {
	const {
		family,
		bold,
		italic,
	} = options;
	let font = family;

	if (
		bold
		&& italic
	) {
		font += ' BoldItalic';
	} else if (bold) {
		font += ' Bold';
	} else if (italic) {
		font += ' Italic';
	}

	return font;
}

export function pixelToPoint(
	pixels: number,
	ppi = 150,
): number {
	return pixels * 72 / ppi;
}

export function pointToPixel(
	points: number,
	ppi = 150,
): number {
	return points * ppi / 72;
}
